import React, { useCallback, useEffect, useMemo } from 'react';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Select } from '@televet/kibble-ui/build/components/Select';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { useGetAppointmentModalAppointmentRoomsLazyQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAppointmentDraft, updateAppointmentModalErrors } from 'pages/Appointments/state/appointmentsModalSlice';
import useClinicUser from 'shared/hooks/useClinicUser';
import * as Sentry from '@sentry/react';
import { LinkedAutomationUpdates } from '..';

const DEFAULT_OPTION: MenuItemProps = { label: 'None', value: 'None' };

interface AppointmentRoomProps {
  updateLinkedAutomations: (updates: LinkedAutomationUpdates) => Promise<void>;
}

export const AppointmentRoom = ({ updateLinkedAutomations }: AppointmentRoomProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { appointmentDraft, appointmentId, errors } = useAppSelector((state) => state.appointmentsModal);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const [getAppointmentRooms, { data: appointmentRoomsData, loading: isLoadingAppointmentRooms }] =
    useGetAppointmentModalAppointmentRoomsLazyQuery({
      variables: { clinicId: currentClinicId || '' },
      onError: (e) => {
        toast({
          title: 'Sorry!',
          description: 'We are having trouble finding your appointment rooms',
          status: 'error',
        });
        Sentry.captureException(e);
      },
    });

  const clinicRoomsOptions: MenuItemProps[] = useMemo(() => {
    if (appointmentRoomsData?.clinicRooms) {
      return [DEFAULT_OPTION].concat(
        appointmentRoomsData.clinicRooms.map((room) => {
          return {
            label: room.name,
            value: room.id,
            isSelected: room.id === appointmentDraft?.roomId,
          };
        }),
      );
    }
    return [];
  }, [appointmentDraft?.roomId, appointmentRoomsData?.clinicRooms]);

  const handleUpdateRoom = useCallback(
    (selectedItem) => {
      dispatch(
        updateAppointmentDraft({
          roomId: selectedItem.value === 'None' ? null : selectedItem.value,
        }),
      );
      dispatch(updateAppointmentModalErrors(errors.filter((error) => error !== 'missingRoom')));
      updateLinkedAutomations({ roomId: selectedItem.value });
    },
    [dispatch, errors, updateLinkedAutomations],
  );

  const handleOnMenuOpen = useCallback(async () => {
    if (appointmentId) return;

    await getAppointmentRooms();
  }, [appointmentId, getAppointmentRooms]);

  useEffect(() => {
    // If editing appointment, fetch appointment rooms on load so we can render selected value
    if (appointmentId) {
      getAppointmentRooms();
    }
  }, [appointmentId, getAppointmentRooms]);

  return (
    <Select
      className="AppointmentModal__AppointmentRoom"
      data-testid="appointment-room-select"
      listProps={{
        isSearchable: true,
        onSelect: handleUpdateRoom,
        maxH: '250px',
        isLoading: isLoadingAppointmentRooms,
      }}
      containerProps={{
        onOpen: handleOnMenuOpen,
      }}
      defaultLeftElement={<Icon color="text.default" size="md" name="locationMarker" />}
      labelStyle={{ fontWeight: 'bold' }}
      value={appointmentDraft?.roomId}
      placeholder="Select a room"
      label="Appointment Room"
      options={clinicRoomsOptions}
      isInvalid={errors.includes('missingRoom')}
      errorText="Please select a room"
    />
  );
};
